/** Popover next to the trash button; `getRoot` returns the overview element it is appended to. */
function createInsuranceDeleteBlockedPopover(getRoot) {
  let blocked = null

  function closeBlocked({ restoreFocus = false } = {}) {
    if (!blocked) return
    const { pop, trigger, cleanup } = blocked
    blocked = null
    cleanup()
    pop.remove()
    trigger.removeAttribute('aria-expanded')
    if (restoreFocus && trigger.isConnected) trigger.focus()
  }

  // Deleting is refused while automations use the policy; explain it next to the trash button.
  function showDeleteBlocked(trigger, schedules, tasks) {
    closeBlocked()
    const automationText = `${insuranceCount(schedules.length, 'aktive Aufgabenautomatisierung', 'aktive Aufgabenautomatisierungen')} (${referenceTitles(schedules)})`
    const taskText = tasks.length
      ? ` und ${insuranceCount(tasks.length, 'offene Aufgabe', 'offene Aufgaben')}`
      : ''
    const pop = document.createElement('div')
    pop.className = 'lux-ins-blocked'
    pop.setAttribute('role', 'alertdialog')
    pop.setAttribute('aria-labelledby', 'luxInsuranceBlockedTitle')
    pop.setAttribute('aria-describedby', 'luxInsuranceBlockedText')
    pop.setAttribute('data-insurance-delete-blocked', '')
    const automator = document.getElementById('TaskAutomatorSection')
    pop.innerHTML = `<p class="lux-ins-blocked-title">${insuranceOverviewIcon('ban')}<span id="luxInsuranceBlockedTitle">Police kann nicht gelöscht werden</span></p>
      <p class="lux-ins-blocked-text" id="luxInsuranceBlockedText">${escapeHtml(`${automationText}${taskText} ${schedules.length + tasks.length === 1 ? 'ist' : 'sind'} verknüpft. Bitte die Automatisierungen zuerst deaktivieren oder einer anderen Versicherung zuordnen.`)}</p>
      <div class="lux-ins-blocked-actions">${automator ? '<a href="#TaskAutomatorSection" data-insurance-show-references>Verknüpfungen anzeigen</a>' : ''}<button type="button" data-insurance-blocked-close>Schließen</button></div>`
    getRoot().appendChild(pop)
    const rect = trigger.getBoundingClientRect()
    pop.style.left = `${Math.round(Math.min(Math.max(rect.right - pop.offsetWidth, 8), window.innerWidth - pop.offsetWidth - 8))}px`
    pop.style.top = `${Math.round(rect.bottom + 8 + pop.offsetHeight > window.innerHeight ? rect.top - 8 - pop.offsetHeight : rect.bottom + 8)}px`
    trigger.setAttribute('aria-expanded', 'true')
    const onPointer = (event) => {
      if (!pop.contains(event.target) && !trigger.contains(event.target)) {
        closeBlocked()
      }
    }
    const onKey = (event) => {
      if (event.key === 'Escape') closeBlocked({ restoreFocus: true })
    }
    const onScroll = () => closeBlocked()
    pop.querySelector('[data-insurance-blocked-close]').onclick = () =>
      closeBlocked({ restoreFocus: true })
    const show = pop.querySelector('[data-insurance-show-references]')
    if (show) {
      show.onclick = (event) => {
        event.preventDefault()
        closeBlocked()
        automator.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }
    const listenTimer = setTimeout(() => {
      document.addEventListener('mousedown', onPointer, true)
      document.addEventListener('keydown', onKey, true)
      window.addEventListener('scroll', onScroll, true)
    }, 0)
    blocked = {
      pop,
      trigger,
      cleanup() {
        clearTimeout(listenTimer)
        document.removeEventListener('mousedown', onPointer, true)
        document.removeEventListener('keydown', onKey, true)
        window.removeEventListener('scroll', onScroll, true)
      },
    }
    pop
      .querySelector('[data-insurance-blocked-close]')
      .focus({ preventScroll: true })
  }

  return {
    show: showDeleteBlocked,
    close: closeBlocked,
    isOpen: () => Boolean(blocked),
  }
}

/**
 * Delete and end/reactivate actions for one overview lifecycle. The lifecycle keeps rows, root and the
 * refresh request; this factory owns the pending confirmation and the delete request.
 */
function createInsuranceRowActions({
  apiUrl,
  buildings,
  request,
  isDisposed,
  isCurrentPage,
  getRoot,
  getRows,
  setRows,
  renderRows,
  refresh,
  abortRefresh,
  showDeleteBlocked,
}) {
  let deleting = false
  let deleteRequest = null
  let confirmation = null

  function setRowBusy(trigger) {
    const row = trigger.closest('[data-insurance-id]')
    row?.classList.add('lux-ins-row-busy')
    row?.querySelectorAll('.lux-ins-action').forEach((button) => {
      button.disabled = true
    })
    trigger.innerHTML =
      '<span class="lux-ins-spinner" aria-hidden="true"></span>'
  }

  function restoreRow(policy, selector) {
    renderRows()
    const row = [...getRoot().querySelectorAll('[data-insurance-id]')].find(
      (node) => node.dataset.insuranceId === String(policy.id)
    )
    row?.querySelector(selector)?.focus()
  }

  async function confirmDelete(policy, trigger) {
    if (confirmation || deleting) return
    const controller = new AbortController()
    confirmation = controller
    trigger.disabled = true
    // The server enforces the schedule block; a failed lookup only loses the advance notice.
    const references = await request(
      `${apiUrl}/${encodeURIComponent(policy.id)}/references`,
      { signal: controller.signal }
    ).catch(() => null)
    if (trigger.isConnected) trigger.disabled = false
    if (confirmation !== controller) return
    if (isDisposed() || !isCurrentPage()) {
      confirmation = null
      return
    }
    const schedules = references?.activeSchedules || []
    const tasks = references?.openTasks || []
    if (schedules.length) {
      confirmation = null
      getRoot().querySelector('[data-insurance-status]').textContent =
        `Die Versicherung wird noch von aktiven Aufgabenautomatisierungen verwendet: ${referenceTitles(schedules)}. Bitte diese zuerst deaktivieren oder einer anderen Versicherung zuordnen.`
      if (trigger.isConnected) showDeleteBlocked(trigger, schedules, tasks)
      return
    }
    const taskWarning = tasks.length
      ? ` Offene Aufgaben verweisen auf diese Police (${referenceTitles(tasks)}); der Verweis entfällt.`
      : ''
    const deletedDocuments = references?.documents?.deleted?.length ?? 0
    const keptDocuments = references?.documents?.kept?.length ?? 0
    const documentNotice = !references?.documents
      ? ' Dokumente, die nirgends sonst verknüpft sind, werden ebenfalls gelöscht.'
      : [
          deletedDocuments
            ? ` ${insuranceCount(deletedDocuments, 'Dokument wird', 'Dokumente werden')} ebenfalls gelöscht, auch im DMS.`
            : '',
          keptDocuments
            ? ` ${insuranceCount(keptDocuments, 'Dokument bleibt', 'Dokumente bleiben')} erhalten, weil ${keptDocuments === 1 ? 'es' : 'sie'} noch anderweitig verknüpft ${keptDocuments === 1 ? 'ist' : 'sind'}.`
            : '',
        ].join('')
    const confirmed = await confirmPopover(trigger, {
      title: 'Versicherung löschen?',
      message: `${policy.policyNumber || 'Versicherung'} – ${insuranceCoverageLabel(policy, buildings)}. Die Police wird für den gesamten Geltungsbereich entfernt.${documentNotice}${taskWarning}`,
      signal: controller.signal,
    })
    if (confirmation !== controller) return
    confirmation = null
    if (isDisposed() || !isCurrentPage()) return
    if (!confirmed) {
      if (trigger.isConnected) trigger.focus()
      return
    }
    deleting = true
    const deleteController = new AbortController()
    deleteRequest = deleteController
    abortRefresh()
    setRowBusy(trigger)
    let deleted = false
    try {
      const result = await request(
        `${apiUrl}/${encodeURIComponent(policy.id)}`,
        { method: 'DELETE', signal: deleteController.signal }
      )
      if (isDisposed() || deleteController.signal.aborted || !isCurrentPage()) {
        return
      }
      setRows(getRows().filter((row) => row.id !== policy.id))
      deleted = true
      if (getRoot()?.isConnected) {
        renderRows()
        const removedDocuments = result?.documents?.deleted?.length ?? 0
        showToast(
          removedDocuments
            ? `Versicherung und ${insuranceCount(removedDocuments, 'Dokument', 'Dokumente')} gelöscht.`
            : 'Versicherung gelöscht.',
          'success'
        )
        getRoot().focus({ preventScroll: true })
      }
    } catch (error) {
      if (
        !isDisposed() &&
        !deleteController.signal.aborted &&
        isCurrentPage() &&
        getRoot()?.isConnected
      ) {
        getRoot().querySelector('[data-insurance-status]').textContent =
          error.message
        showToast(error.message, 'error')
        restoreRow(policy, '[data-delete-insurance]')
      }
    } finally {
      if (deleteRequest === deleteController) deleteRequest = null
      deleting = false
      if (trigger.isConnected) trigger.disabled = false
      if (deleted && !isDisposed()) void refresh()
    }
  }

  async function changeStatus(policy, trigger, status) {
    if (confirmation || deleting) return
    const controller = new AbortController()
    confirmation = controller
    const reference = policy.policyNumber || 'Versicherung'
    const ending = status === 'ended'
    const confirmed = await confirmPopover(trigger, {
      title: ending ? 'Police beenden?' : 'Police reaktivieren?',
      message: ending
        ? `${reference} wird als beendet markiert. Police und Dokumente bleiben erhalten, sie zählt aber nicht mehr als aktive Deckung und wird in Vorgängen nicht mehr angeboten.`
        : `${reference} zählt wieder als aktive Deckung für ihren Geltungsbereich.`,
      confirmLabel: ending ? 'Beenden' : 'Reaktivieren',
      tone: 'default',
      signal: controller.signal,
    })
    if (confirmation !== controller) return
    confirmation = null
    if (isDisposed() || !isCurrentPage()) return
    if (!confirmed) {
      if (trigger.isConnected) trigger.focus()
      return
    }
    abortRefresh()
    setRowBusy(trigger)
    try {
      await request(`${apiUrl}/${encodeURIComponent(policy.id)}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      })
      if (isDisposed() || !isCurrentPage()) return
      setRows(
        getRows().map((row) =>
          row.id === policy.id ? { ...row, status } : row
        )
      )
      if (getRoot()?.isConnected) {
        renderRows()
        showToast(
          ending ? 'Versicherung beendet.' : 'Versicherung reaktiviert.',
          'success'
        )
        void refresh()
      }
    } catch (error) {
      if (!isDisposed() && isCurrentPage() && getRoot()?.isConnected) {
        getRoot().querySelector('[data-insurance-status]').textContent =
          error.message
        showToast(error.message, 'error')
        restoreRow(policy, '[data-insurance-status-toggle]')
      }
    }
  }

  return {
    confirmDelete,
    changeStatus,
    isConfirming: () => Boolean(confirmation),
    isDeleting: () => deleting,
    abortDelete() {
      // Aborting stops client work; a DELETE may already have committed on the server.
      deleteRequest?.abort()
      deleting = false
    },
    abortConfirmation() {
      confirmation?.abort()
      confirmation = null
    },
  }
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    createInsuranceDeleteBlockedPopover,
    createInsuranceRowActions,
  }
}
